"use client";
import { EventJourneyStatus } from "@prisma/client";
import { useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { create } from "zustand";

interface GeoLocation {
    latitude: number;
    longitude: number;
}

type ViewMode = "list" | "grid";

// Type definition
export type EventFilters = {
    pastEvents: boolean;
    onlyShowCurrentUserAttendingEvents: boolean;
    onlyShowCurrentUserHostingEvents: boolean;
    includeAttendeesNames: boolean;
    includeHostNames: boolean;
    geoLocation: undefined | GeoLocation;
    useDateSortOverRide: boolean;
    dateRangeFilters:
        | undefined
        | {
              startDate: Date;
              endDate: Date;
          };
    viewMode: ViewMode | undefined;
    status?: EventJourneyStatus;
};

// Initial value
const initialEventFilters: EventFilters = {
    pastEvents: false,
    onlyShowCurrentUserAttendingEvents: false,
    onlyShowCurrentUserHostingEvents: false,
    includeAttendeesNames: false,
    includeHostNames: false,
    geoLocation: undefined,
    useDateSortOverRide: false,
    dateRangeFilters: undefined,
    viewMode: undefined,
    status: undefined,
};

interface FilterState {
    search: string;
    debouncedSearch: string;
    tags: string[];
    eventFilters: typeof initialEventFilters;

    resultSetLength?: number;

    setSearch: (search: string) => void;
    setTags: (tags: string[]) => void;
    setEventFilters: (eventFilters: typeof initialEventFilters) => void;
    setViewMode: (mode: ViewMode) => void;
    setResultSetLength: (length?: number) => void;

    resetFilter: () => void;
}

// Updated debounce function to ensure it returns a function that keeps debounce logic intact
let timeoutId: ReturnType<typeof setTimeout>;
const debounce = (fn: Function, delay: number) => {
    return function (...args: any) {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
};

// Helper function to update URL
const updateURL = (search: string, pastEvents: boolean) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("search", search);
    if (search === "") {
        queryParams.delete("search");
    }
    if (pastEvents) {
        queryParams.set("pastEvents", "true");
    } else {
        queryParams.delete("pastEvents");
    }
    window.history.pushState(null, "", `?${queryParams.toString()}`);
};

const useFilterStore = create<FilterState>((set, get) => ({
    search: "",
    debouncedSearch: "",
    tags: [],
    eventFilters: initialEventFilters,
    viewMode: undefined,
    resultSetLength: undefined,
    setSearch: (search) => {
        set(() => ({ search }));
        debounce(() => {
            const {
                debouncedSearch,
                eventFilters: { pastEvents },
            } = get();
            if (search !== debouncedSearch) {
                set(() => ({ debouncedSearch: search }));
                updateURL(search, pastEvents);
            }
        }, 1000)();
    },
    setTags: (tags) => set(() => ({ tags })),
    setEventFilters: (eventFilters) => {
        set((state) => ({
            eventFilters: { ...state.eventFilters, ...eventFilters },
        }));
        const { search } = get();
        debounce(() => {
            updateURL(search, eventFilters.pastEvents);
        }, 300)();
    },
    setViewMode: (mode: ViewMode) => {
        set((state) => ({
            eventFilters: {
                ...state.eventFilters,
                viewMode: mode,
            },
        }));
        // Update URL
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set("view", mode);
        window.history.pushState(null, "", `?${queryParams.toString()}`);
    },
    setResultSetLength: (length?: number) =>
        set(() => ({ resultSetLength: length })),
    resetFilter: () =>
        set(() => ({ search: "", debouncedSearch: "", tags: [] })),
}));

export function useSyncSearchWithURL() {
    const searchParams = useSearchParams();
    const { setSearch, setEventFilters, setViewMode } = useFilterStore();

    useEffect(() => {
        const search = searchParams?.get("search");
        const pastEvents = searchParams?.get("pastEvents") === "true";
        const view = searchParams?.get("view") as ViewMode;

        if (search && typeof search === "string") {
            setSearch(search);
        }
        if (pastEvents) {
            useFilterStore.setState({
                eventFilters: {
                    ...useFilterStore.getState().eventFilters,
                    pastEvents: true,
                },
            });
        }
        if ((view && view === "list") || view === "grid") {
            setViewMode(view);
        }
    }, [searchParams, setSearch, setEventFilters]);
}

export default useFilterStore;
